import React, { useState } from 'react';
import IntroScreen from './components/IntroScreen';
import SimulatorScreen from './components/SimulatorScreen';
import ContactFormScreen from './components/ContactFormScreen';
import ResultsScreen from './components/ResultsScreen';
import DebugPanel from './components/DebugPanel';
import { Box } from '@mui/material';
import { integrate } from './services/integration';

function App() {
  // Estados de controle de visualização
  const [activeScreen, setActiveScreen] = useState('intro'); // 'intro', 'simulator', 'form', 'result'
  const [selectedAsset, setSelectedAsset] = useState('');
  const [investmentValue, setInvestmentValue] = useState('');
  const [addedAssets, setAddedAssets] = useState([]);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consentTerms: false
  });

  // Criar dados simulados para gráficos iniciais
  const simulatedChartData = [
    { year: 1, traditional: Math.round(9500 * 1 * 1.05), monefica: Math.round(4000 * 1 * 1.03) },
    { year: 2, traditional: Math.round(9500 * 2 * 1.05), monefica: Math.round(4000 * 2 * 1.03) },
    { year: 3, traditional: Math.round(9500 * 3 * 1.05), monefica: Math.round(4000 * 3 * 1.03) },
    { year: 4, traditional: Math.round(9500 * 4 * 1.05), monefica: Math.round(4000 * 4 * 1.03) },
    { year: 5, traditional: Math.round(9500 * 5 * 1.05), monefica: Math.round(4000 * 5 * 1.03) },
    { year: 6, traditional: Math.round(9500 * 6 * 1.05), monefica: Math.round(4000 * 6 * 1.03) },
    { year: 7, traditional: Math.round(9500 * 7 * 1.05), monefica: Math.round(4000 * 7 * 1.03) },
    { year: 8, traditional: Math.round(9500 * 8 * 1.05), monefica: Math.round(4000 * 8 * 1.03) },
    { year: 9, traditional: Math.round(9500 * 9 * 1.05), monefica: Math.round(4000 * 9 * 1.03) },
    { year: 10, traditional: Math.round(9500 * 10 * 1.05), monefica: Math.round(4000 * 10 * 1.03) }
  ];

  const [resultData, setResultData] = useState({
    totalSaved: 58700,
    chartData: simulatedChartData,
    yearlyEconomy: 3900,
    traditionalFee: 9500,
    moneficaFee: 4000,
    totalEconomy: 58700
  });

  // Lista de ativos completa para o dropdown
  const dropdownData = [
    { id: 1, name: 'COE', rendimento: 'Alternativo' },
    { id: 2, name: 'Fundos Renda Fixa e Crédito Privado', rendimento: 'Renda Fixa' },
    { id: 3, name: 'Fundos Multimercado', rendimento: 'Alternativo' },
    { id: 4, name: 'Fundos de Ações', rendimento: 'Renda Variável' },
    { id: 5, name: 'Título Público (vencimento em até 10 anos)', rendimento: 'Renda Fixa' },
    { id: 6, name: 'Título Público (vencimento de mais de 10 anos)', rendimento: 'Renda Fixa' },
    { id: 7, name: 'CDBs, LCIs, LCAs (vencimento de até 5 anos)', rendimento: 'Renda Fixa' },
    { id: 8, name: 'CDBs, LCIs, LCAs (vencimento com mais de 5 anos)', rendimento: 'Renda Fixa' },
    { id: 9, name: 'Crédito Privado: Debêntures, CRIs, CRAs (vencimento de até 5 anos)', rendimento: 'Renda Fixa' },
    { id: 10, name: 'Crédito Privado: Debêntures, CRIs, CRAs (vencimento com mais de 5 anos)', rendimento: 'Renda Fixa' },
    { id: 11, name: 'Ações e ETFs (operação feita pelo Assessor de Investimentos)', rendimento: 'Renda Variável' },
    { id: 12, name: 'Fundos Imobiliários (operação feita pelo Assessor de Investimentos)', rendimento: 'Renda Variável' },
    { id: 13, name: 'Ações e ETFs (operação feita pelo cliente no Home Broker)', rendimento: 'Renda Variável' },
    { id: 14, name: 'Fundos Imobiliários (operação feita pelo cliente no Home Broker)', rendimento: 'Renda Variável' },
    { id: 15, name: 'Previdência Privada PGBL ou VGBL: fundos de renda fixa e crédito privado', rendimento: 'Renda Fixa' },
    { id: 16, name: 'Previdência Privada PGBL ou VGBL: fundos multimercados e de ações', rendimento: 'Renda Variável' },
    { id: 17, name: 'Ofertas e Subscrições: Renda Fixa e Crédito Privado', rendimento: 'Renda Fixa' },
    { id: 18, name: 'Ofertas e Subscrições: Fundos Imobiliários', rendimento: 'Renda Variável' },
    { id: 19, name: 'Ofertas e Subscrições: Ações', rendimento: 'Renda Variável' }
  ];

  // Função para avançar para o simulador
  const handleStartSimulation = () => {
    setActiveScreen('simulator');
  };

  // Função para voltar para a tela anterior
  const handleBack = () => {
    if (activeScreen === 'result') {
      setActiveScreen('simulator');
    } else if (activeScreen === 'form') {
      setActiveScreen('simulator');
    } else if (activeScreen === 'simulator') {
      setActiveScreen('intro');
    }
  };

  const handleAddAsset = () => {
    if (selectedAsset && investmentValue) {
      const asset = dropdownData.find(a => a.id.toString() === selectedAsset);
      if (asset) {
        const newAsset = {
          id: Date.now(),
          name: asset.name,
          value: parseFloat(investmentValue),
          rendimento: asset.rendimento
        };
        const updatedAssets = [...addedAssets, newAsset];
        setAddedAssets(updatedAssets);
        const newTotal = updatedAssets.reduce((sum, asset) => sum + asset.value, 0);
        setTotalInvestment(newTotal);
        setSelectedAsset('');
        setInvestmentValue('');
      }
    }
  };

  const handleRemoveAsset = (assetId) => {
    const updatedAssets = addedAssets.filter(asset => asset.id !== assetId);
    setAddedAssets(updatedAssets);
    const newTotal = updatedAssets.reduce((sum, asset) => sum + asset.value, 0);
    setTotalInvestment(newTotal);
  };

  const handleCalculate = () => {
    const total = addedAssets.reduce((sum, asset) => sum + asset.value, 0);
    setTotalInvestment(total);
    
    // Calcular taxas por tipo de ativo
    const rendaFixaTotal = addedAssets.filter(asset => asset.rendimento === 'Renda Fixa').reduce((sum, asset) => sum + asset.value, 0);
    const rendaVariavelTotal = addedAssets.filter(asset => asset.rendimento === 'Renda Variável').reduce((sum, asset) => sum + asset.value, 0);
    const alternativoTotal = addedAssets.filter(asset => asset.rendimento === 'Alternativo').reduce((sum, asset) => sum + asset.value, 0);
    const outrosTotal = addedAssets.filter(asset => !['Renda Fixa', 'Renda Variável', 'Alternativo'].includes(asset.rendimento)).reduce((sum, asset) => sum + asset.value, 0);
    
    // Taxas tradicionais por tipo
    const rendaFixaTaxa = 0.008;  // 0.8%
    const rendaVariavelTaxa = 0.015; // 1.5%
    const alternativoTaxa = 0.012; // 1.2%
    const outrosTaxa = 0.012; // 1.2%
    
    // Taxa tradicional média ponderada
    let traditionalFeeRate;
    if (total === 0) {
      traditionalFeeRate = 0.011; // 1.1% default
    } else {
      traditionalFeeRate = (
        (rendaFixaTotal * rendaFixaTaxa) + 
        (rendaVariavelTotal * rendaVariavelTaxa) + 
        (alternativoTotal * alternativoTaxa) +
        (outrosTotal * outrosTaxa)
      ) / total;
      
      // Garantir que esteja entre 0.6% e 1.5%
      traditionalFeeRate = Math.max(0.006, Math.min(0.015, traditionalFeeRate));
    }
    
    // Taxa fixa da Monefica
    const moneficaFeeRate = 0.005; // 0.5%
    
    // Projeção de crescimento anual (variáveis não utilizadas removidas)
    const chartData = [];
    let traditionalCumulativeFees = 0;
    let moneficaCumulativeFees = 0;
    
    // Calcular para cada ano
    for (let year = 1; year <= 10; year++) {
      // Taxa anual sobre o patrimônio inicial (não acumulado)
      const traditionalYearFee = total * traditionalFeeRate;
      const moneficaYearFee = total * moneficaFeeRate;
      
      // Acumular taxas
      traditionalCumulativeFees += traditionalYearFee;
      moneficaCumulativeFees += moneficaYearFee;
      
      chartData.push({
        year,
        traditional: Math.round(traditionalCumulativeFees),
        monefica: Math.round(moneficaCumulativeFees)
      });
    }
    
    // Valores anuais das taxas
    const yearlyTraditionalFee = Math.round(total * traditionalFeeRate);
    const yearlyMoneficaFee = Math.round(total * moneficaFeeRate);
    const yearlyEconomy = yearlyTraditionalFee - yearlyMoneficaFee;
    const totalEconomy = Math.round(traditionalCumulativeFees - moneficaCumulativeFees);
    
    setResultData({
      totalSaved: totalEconomy,
      chartData,
      yearlyEconomy,
      traditionalFee: yearlyTraditionalFee,
      moneficaFee: yearlyMoneficaFee,
      totalEconomy,
      totalInvestment: total
    });
    
    setActiveScreen('form');
  };

  const handleFormChange = (event) => {
    const { name, value, checked } = event.target;
    setFormData({
      ...formData,
      [name]: name === 'consentTerms' ? checked : value
    });
  };

  const handleSubmitForm = () => {
    // Validação dos campos obrigatórios
    if (!formData.name || !formData.email || !formData.consentTerms) {
      alert('Por favor, preencha todos os campos e aceite os termos.');
      return;
    }

    setIsSubmitting(true);

    // Sempre tentar integração para testar
    // Para desabilitar, altere a condição abaixo
    const shouldIntegrate = true; // process.env.NODE_ENV !== 'development'
    
    if (shouldIntegrate) {
      integrate(resultData, formData)
        .then(() => {
          console.log('✅ Dados enviados com sucesso');
          setActiveScreen('result');
        })
        .catch((error) => {
          console.error('❌ Erro ao enviar dados:', error);
          alert('Ocorreu um erro ao enviar os dados. Por favor, tente novamente.');
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      // Em desenvolvimento, pula a integração
      console.log('Modo desenvolvimento - dados não enviados');
      setTimeout(() => {
        setActiveScreen('result');
        setIsSubmitting(false);
      }, 1000); // Simula delay para testar o loading
    }
  };

  return (
    <Box sx={{ background: 'linear-gradient(180deg, #121C42 0%, #0A1029 100%)', minHeight: '100vh', width: '100%' }}>
      {activeScreen === 'intro' && (
        <IntroScreen handleStartSimulation={handleStartSimulation} />
      )}
      {activeScreen === 'simulator' && (
        <SimulatorScreen
          selectedAsset={selectedAsset}
          setSelectedAsset={setSelectedAsset}
          investmentValue={investmentValue}
          setInvestmentValue={setInvestmentValue}
          addedAssets={addedAssets}
          setAddedAssets={setAddedAssets}
          totalInvestment={totalInvestment}
          isSelectOpen={isSelectOpen}
          setIsSelectOpen={setIsSelectOpen}
          dropdownData={dropdownData}
          handleAddAsset={handleAddAsset}
          handleRemoveAsset={handleRemoveAsset}
          handleCalculate={handleCalculate}
          handleBack={handleBack}
        />
      )}
      {activeScreen === 'form' && (
        <ContactFormScreen
          formData={formData}
          handleFormChange={handleFormChange}
          handleSubmitForm={handleSubmitForm}
          handleBack={handleBack}
          isSubmitting={isSubmitting}
        />
      )}
      {activeScreen === 'result' && (
        <ResultsScreen
          resultData={resultData}
          handleBack={handleBack}
        />
      )}
      
      {/* Debug Panel - apenas em desenvolvimento */}
      {/* {process.env.NODE_ENV === 'development' && <DebugPanel />} */}
    </Box>
  );
}

export default App;